import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import {
  Container, Paper, Typography, TextField, Table, TableBody, TableCell,
  TableHead, TableRow, TablePagination, Grid, Chip, Button, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Select, InputLabel, FormControl, Divider, makeStyles
} from "@material-ui/core";
import { Refresh, GetApp, WhatsApp, VideoCall } from "@material-ui/icons";
import { getAuditLogs, exportAuditLogs } from "../../services/api.new-features";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  paper: { padding: theme.spacing(2), marginTop: theme.spacing(2), borderRadius: 14 },
  filters: { marginBottom: theme.spacing(2) },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: theme.spacing(2), gap: theme.spacing(1), flexWrap: "wrap"
  },
  statCard: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.08)",
    background: "#fff"
  },
  statLabel: {
    fontSize: 12,
    color: "#757575",
    marginBottom: theme.spacing(0.5)
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.1
  },
  detailsCell: {
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer",
    color: "#1565c0",
    textDecoration: "underline dotted"
  },
  detailsFull: {
    fontFamily: "monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    background: "#f5f5f5",
    padding: 12,
    borderRadius: 4,
    maxHeight: 420,
    overflowY: "auto"
  },
  liveIndicator: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#4caf50",
    marginRight: 6,
    animation: "$pulse 2s infinite"
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5)
  },
  quickBtn: {
    textTransform: "none",
    borderRadius: 20
  },
  sourceChip: {
    fontWeight: 700,
    minWidth: 88
  },
  "@keyframes pulse": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.4 },
    "100%": { opacity: 1 }
  }
}));

const ACTION_COLORS = {
  create: "#4caf50",
  update: "#2196f3",
  delete: "#f44336",
  login: "#ff9800",
  login_success: "#4caf50",
  login_failed: "#f44336",
  logout: "#9e9e9e",
  bbb_meeting_create: "#7b1fa2",
  bbb_hearing_create: "#880e4f",
  bbb_meeting_end: "#4a148c",
  protocol_generated: "#0288d1",
  priority_change: "#f57c00",
  transfer: "#00796b",
  ticket_close_command: "#546e7a",
  reminder_create: "#6a1b9a"
};

const ACTION_LABELS = {
  bbb_meeting_create: "BBB: Reunião criada",
  bbb_hearing_create: "BBB: Audiência criada",
  bbb_meeting_end: "BBB: Reunião encerrada",
  protocol_generated: "Protocolo gerado",
  priority_change: "Prioridade alterada",
  transfer: "Transferência",
  ticket_close_command: "Ticket encerrado (cmd)",
  reminder_create: "Lembrete criado",
  login_success: "Login ok",
  login_failed: "Login falhou"
};

const safeParseDetails = (details) => {
  if (!details) return {};
  if (typeof details === "object") return details;
  try {
    return JSON.parse(details);
  } catch (_) {
    return { raw: details };
  }
};

const getActionColor = (action) => ACTION_COLORS[action] || "#757575";
const formatAction = (action) => ACTION_LABELS[action] || action;

const getSource = (log) => {
  const details = safeParseDetails(log.details);
  if (details.source === "whatsapp_command" || log.ipAddress === "whatsapp") return "WhatsApp";
  if (details.source === "bbb_panel") return "Painel BBB";
  if (String(log.ipAddress || "").includes("panel")) return "Painel";
  if (log.ipAddress) return "Web/API";
  return "Sistema";
};

const getFriendlyUser = (log) => {
  if (log.user?.name) return log.user.name;
  const source = getSource(log);
  if (source === "WhatsApp") return "Cliente / WhatsApp";
  if (source === "Painel BBB") return "Operador / Painel";
  return "Sistema";
};

const getDetailsPreview = (log) => {
  const details = safeParseDetails(log.details);
  if (log.action === "bbb_meeting_create") {
    return `${details.meetingName || "Reunião"} • ${details.meetingID || log.entityId}`;
  }
  if (log.action === "bbb_hearing_create") {
    return `${details.hearingName || "Audiência"} • ${details.meetingID || log.entityId}`;
  }
  if (log.action === "bbb_meeting_end") {
    return `Sessão encerrada • ${details.meetingID || log.entityId}`;
  }
  if (details.protocol) return `Protocolo ${details.protocol}`;
  if (details.reason) return `Motivo: ${details.reason}`;
  if (details.reminderMsg) return `Lembrete: ${details.reminderMsg}`;
  if (details.queueName) return `Fila: ${details.queueName}`;
  const raw = typeof log.details === "string" ? log.details : JSON.stringify(log.details || {});
  return raw.slice(0, 90) + (raw.length > 90 ? "…" : "");
};

const sourceChipStyle = (source) => {
  if (source === "WhatsApp") return { backgroundColor: "#e8f5e9", color: "#2e7d32" };
  if (source === "Painel BBB") return { backgroundColor: "#ede7f6", color: "#5e35b1" };
  if (source === "Painel") return { backgroundColor: "#e3f2fd", color: "#1565c0" };
  return { backgroundColor: "#f5f5f5", color: "#616161" };
};

const AuditLog = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [entity, setEntity] = useState("");
  const [userId, setUserId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [users, setUsers] = useState([]);
  const [detailDialog, setDetailDialog] = useState(null);
  const [liveCount, setLiveCount] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    api.get("/users/list")
      .then(res => setUsers(res.data?.users || res.data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async (silent = false) => {
    try {
      const params = { pageNumber: page + 1, searchParam: search, entity, userId: userId || undefined };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate + "T23:59:59";
      const res = await getAuditLogs(params);
      setLogs(res.data.logs || []);
      setCount(res.data.count || 0);
    } catch (e) {
      if (!silent) toast.error("Erro ao carregar logs");
    }
  }, [page, search, entity, userId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    intervalRef.current = setInterval(() => load(true), 30000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  useEffect(() => {
    if (!user?.companyId || !socketManager) return;
    const socket = socketManager.getSocket(user.companyId);
    if (!socket) return;
    const companyId = user.companyId;

    const onAuditEvent = (data) => {
      if (data.action === "create" && data.log) {
        setLiveCount(c => c + 1);
        if (page === 0) {
          setLogs(prev => [data.log, ...prev.slice(0, 49)]);
          setCount(c => c + 1);
        }
      }
    };

    socket.on(`company-${companyId}-audit`, onAuditEvent);
    return () => socket.off(`company-${companyId}-audit`, onAuditEvent);
  }, [user, socketManager, page]);

  const handleExport = async () => {
    try {
      const params = {};
      if (search) params.searchParam = search;
      if (entity) params.entity = entity;
      if (userId) params.userId = userId;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate + "T23:59:59";
      const response = await exportAuditLogs(params);
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `audit-log-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erro ao exportar");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setEntity("");
    setUserId("");
    setStartDate("");
    setEndDate("");
    setPage(0);
  };

  const setQuickFilter = (mode) => {
    setPage(0);
    if (mode === "all") {
      setSearch("");
      setEntity("");
      return;
    }
    if (mode === "bbb") {
      setSearch("bbb_");
      setEntity("");
      return;
    }
    if (mode === "ticket") {
      setSearch("");
      setEntity("Ticket");
      return;
    }
    if (mode === "auth") {
      setSearch("login");
      setEntity("");
    }
  };

  const metrics = useMemo(() => {
    const bbbCount = logs.filter(log => String(log.action || "").startsWith("bbb_")).length;
    const whatsappCount = logs.filter(log => getSource(log) === "WhatsApp").length;
    const systemCount = logs.filter(log => !log.user?.name).length;
    return { bbbCount, whatsappCount, systemCount };
  }, [logs]);

  const detailJson = useMemo(() => {
    if (!detailDialog) return "Sem detalhes";
    try {
      return JSON.stringify(safeParseDetails(detailDialog.details), null, 2);
    } catch (_) {
      return String(detailDialog.details || "Sem detalhes");
    }
  }, [detailDialog]);

  return (
    <Container maxWidth="lg" className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ display: "flex", alignItems: "center" }}>
          Log de Auditoria
          {liveCount > 0 && (
            <span style={{ marginLeft: 10 }}>
              <span className={classes.liveIndicator} />
              <Typography component="span" variant="caption" style={{ color: "#4caf50" }}>
                +{liveCount} novos
              </Typography>
            </span>
          )}
        </Typography>
        <div style={{ display: "flex", gap: 8 }}>
          <Tooltip title="Exportar CSV">
            <Button variant="outlined" size="small" startIcon={<GetApp />} onClick={handleExport}>
              Exportar
            </Button>
          </Tooltip>
          <Tooltip title="Atualizar">
            <IconButton size="small" onClick={() => { setLiveCount(0); load(false); }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <Grid container spacing={2} style={{ marginBottom: 8 }}>
        <Grid item xs={12} sm={4}>
          <Paper className={classes.statCard}>
            <div className={classes.statLabel}>Registros nesta página</div>
            <div className={classes.statValue}>{logs.length}</div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper className={classes.statCard}>
            <div className={classes.statLabel}>Eventos BBB visíveis</div>
            <div className={classes.statValue}>{metrics.bbbCount}</div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper className={classes.statCard}>
            <div className={classes.statLabel}>Origem WhatsApp</div>
            <div className={classes.statValue}>{metrics.whatsappCount}</div>
          </Paper>
        </Grid>
      </Grid>

      <Paper className={classes.paper}>
        <div className={classes.filterRow}>
          <Button className={classes.quickBtn} variant="outlined" size="small" onClick={() => setQuickFilter("all")}>Todos</Button>
          <Button className={classes.quickBtn} variant="outlined" size="small" startIcon={<VideoCall />} onClick={() => setQuickFilter("bbb")}>Somente BBB</Button>
          <Button className={classes.quickBtn} variant="outlined" size="small" startIcon={<WhatsApp />} onClick={() => setQuickFilter("ticket")}>Tickets</Button>
          <Button className={classes.quickBtn} variant="outlined" size="small" onClick={() => setQuickFilter("auth")}>Autenticação</Button>
        </div>
        <Divider style={{ marginBottom: 16 }} />

        <Grid container spacing={2} className={classes.filters}>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Buscar ação/entidade" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} size="small" />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField fullWidth label="Entidade" value={entity} onChange={e => { setEntity(e.target.value); setPage(0); }} size="small" placeholder="Ticket, User..." />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Usuário</InputLabel>
              <Select value={userId} onChange={e => { setUserId(e.target.value); setPage(0); }}>
                <MenuItem value=""><em>Todos</em></MenuItem>
                {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField fullWidth type="date" label="De" InputLabelProps={{ shrink: true }} value={startDate} onChange={e => { setStartDate(e.target.value); setPage(0); }} size="small" />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField fullWidth type="date" label="Até" InputLabelProps={{ shrink: true }} value={endDate} onChange={e => { setEndDate(e.target.value); setPage(0); }} size="small" />
          </Grid>
          <Grid item xs={12} sm={1} style={{ display: "flex", alignItems: "flex-end" }}>
            <Button size="small" onClick={resetFilters} style={{ color: "#9e9e9e" }}>Limpar</Button>
          </Grid>
        </Grid>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Ação</TableCell>
              <TableCell>Entidade</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map(log => {
              const source = getSource(log);
              return (
                <TableRow key={log.id} hover>
                  <TableCell style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                    {new Date(log.createdAt).toLocaleString("pt-BR")}
                  </TableCell>
                  <TableCell style={{ fontSize: 12 }}>{getFriendlyUser(log)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={formatAction(log.action)} style={{ backgroundColor: getActionColor(log.action), color: "#fff", fontSize: 11 }} />
                  </TableCell>
                  <TableCell style={{ fontSize: 12 }}>{log.entity}</TableCell>
                  <TableCell style={{ fontSize: 12 }}>{log.entityId}</TableCell>
                  <TableCell>
                    <Chip size="small" label={source} className={classes.sourceChip} style={sourceChipStyle(source)} />
                  </TableCell>
                  <TableCell>
                    {log.details ? (
                      <span className={classes.detailsCell} onClick={() => setDetailDialog(log)} title="Clique para ver detalhes completos">
                        {getDetailsPreview(log)}
                      </span>
                    ) : "-"}
                  </TableCell>
                </TableRow>
              );
            })}
            {logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ color: "#9e9e9e", padding: 32 }}>
                  Nenhum registro encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination component="div" count={count} page={page} rowsPerPage={50} onPageChange={(e, p) => setPage(p)} rowsPerPageOptions={[50]} labelDisplayedRows={({ from, to, count: total }) => `${from}–${to} de ${total}`} />
      </Paper>

      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalhes do Log — {detailDialog?.action}
          <Typography variant="caption" color="textSecondary" style={{ marginLeft: 12 }}>
            {detailDialog && new Date(detailDialog.createdAt).toLocaleString("pt-BR")}
            {detailDialog && ` — ${getFriendlyUser(detailDialog)}`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div className={classes.detailsFull}>{detailJson}</div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AuditLog;
